import {useEffect} from 'react';

import {zodResolver} from '@hookform/resolvers/zod';
import _ from '@lodash';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import {AxiosError} from 'axios';
import {Controller, useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {z} from 'zod';

import useJwtAuth from '../useJwtAuth';

/**
 * Form Validation Schema
 */
const schema = z.object({
  email: z
    .string()
    .email('You must enter a valid email')
    .nonempty('You must enter an email'),
  password: z
    .string()
    .min(4, 'Password is too short - must be at least 4 chars.')
    .nonempty('Please enter your password.'),
});

type FormType = {
  email: string;
  password: string;
  remember?: boolean;
};

const defaultValues = {
  email: '',
  password: '',
  remember: true,
};

function JwtSignInForm() {
  const {signIn} = useJwtAuth();

  const {control, formState, handleSubmit, setValue, setError} =
    useForm<FormType>({
      mode: 'onChange',
      defaultValues,
      resolver: zodResolver(schema),
    });

  const {isValid, dirtyFields, errors} = formState;

  useEffect(() => {
    setValue('email', 'admin@fusetheme.com', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('password', '5;4+0IOx:\\Dy', {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue]);

  function onSubmit(formData: FormType) {
    const {email, password} = formData;

    signIn({
      email,
      password,
    }).catch(
      (
        error: AxiosError<
          {
            type: 'email' | 'password' | 'remember' | `root.${string}` | 'root';
            message: string;
          }[]
        >,
      ) => {
        const errorData = error.response.data;

        errorData.forEach(err => {
          setError(err.type, {
            type: 'manual',
            message: err.message,
          });
        });
      },
    );
  }

  return (
    <form
      name="loginForm"
      noValidate
      className="mt-32 flex w-full flex-col justify-center"
      onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="email"
        control={control}
        render={({field}) => (
          <TextField
            {...field}
            className="mb-24"
            label="Email"
            autoFocus
            type="email"
            error={!!errors.email}
            helperText={errors?.email?.message}
            variant="outlined"
            required
            fullWidth
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        render={({field}) => (
          <TextField
            {...field}
            className="mb-24"
            label="Password"
            type="password"
            error={!!errors.password}
            helperText={errors?.password?.message}
            variant="outlined"
            required
            fullWidth
          />
        )}
      />

      <div className="flex flex-col items-center justify-center sm:flex-row sm:justify-between">
        <Controller
          name="remember"
          control={control}
          render={({field}) => (
            <FormControl>
              <FormControlLabel
                label="Remember me"
                control={
                  <Checkbox
                    size="small"
                    {...field}
                  />
                }
              />
            </FormControl>
          )}
        />

        <Link
          className="text-md font-medium"
          to="/pages/auth/forgot-password">
          Forgot password?
        </Link>
      </div>

      <Button
        variant="contained"
        color="secondary"
        className=" mt-16 w-full"
        aria-label="Sign in"
        disabled={_.isEmpty(dirtyFields) || !isValid}
        type="submit"
        size="large">
        Sign in
      </Button>
    </form>
  );
}

export default JwtSignInForm;
